import { getCsrfToken } from "@/lib/csrf"

export type AgentActivityProcessKind = "image" | "video" | "text" | "agent_reply" | "batch"

export type AgentActivityScope = {
  kind: "project" | "company"
  id: number
}

export type AgentActivityDeepLink = {
  surface: string
  entityId?: number
  shotId?: number
}

export type AgentActivityProcessState = "running" | "done" | "idle"

export type AgentActivityProcess = {
  kind: AgentActivityProcessKind | string
  state: AgentActivityProcessState
}

export type AgentActivityRow = {
  slot_key: string
  mode: string
  entity_kind: string
  entity_id: number
  title: string
  process_kind: AgentActivityProcessKind | string
  processes: AgentActivityProcess[]
  reply_state: AgentActivityProcessState | null
  status_bucket: "ready_unread" | "in_progress" | "ready_viewed" | "inactive"
  last_message_preview: string | null
  last_message_role: string | null
  activity_at: string | null
  conversation_id: number | null
  thumb_url: string | null
  deep_link: AgentActivityDeepLink
}

export type AgentActivityResponse = {
  pinned: AgentActivityRow[]
  ready_unread: AgentActivityRow[]
  in_progress: AgentActivityRow[]
  ready_viewed: AgentActivityRow[]
  inactive: {
    items: AgentActivityRow[]
    page: number
    per_page: number
    total: number
    has_more: boolean
  }
  counts: {
    ready_unread: number
    in_progress: number
    ready_viewed: number
    inactive: number
  }
}

export function agentActivityScopeKey(scope: AgentActivityScope): string {
  return `${scope.kind}:${scope.id}`
}

function activityBasePath(scope: AgentActivityScope): string {
  if (scope.kind === "company") return "/company/agent_activity"
  return `/projects/${scope.id}/workspace/agent_activity`
}

/** Coalesce concurrent identical GETs (toolbar badge + Agents drawer on same nonce). */
const inflightAgentActivity = new Map<string, Promise<AgentActivityResponse>>()

export async function fetchAgentActivity(scope: AgentActivityScope, page = 1): Promise<AgentActivityResponse> {
  const key = `${agentActivityScopeKey(scope)}:${page}`
  const hit = inflightAgentActivity.get(key)
  if (hit) return hit

  const req = (async () => {
    const res = await fetch(`${activityBasePath(scope)}?page=${page}`, {
      headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      credentials: "same-origin",
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return (await res.json()) as AgentActivityResponse
  })().finally(() => {
    if (inflightAgentActivity.get(key) === req) inflightAgentActivity.delete(key)
  })

  inflightAgentActivity.set(key, req)
  return req
}

export async function markAgentActivityViewed(params: {
  scope: AgentActivityScope
  mode: string
  entityKind: string
  entityId: number
}): Promise<void> {
  const res = await fetch(`${activityBasePath(params.scope)}/mark_viewed`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    credentials: "same-origin",
    body: JSON.stringify({
      mode: params.mode,
      entity_kind: params.entityKind,
      entity_id: params.entityId,
    }),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
}

export async function setAgentActivityPinned(params: {
  scope: AgentActivityScope
  slotKey: string
  pinned: boolean
}): Promise<{ ok: true; pinned_slot_keys: string[] }> {
  const res = await fetch(`${activityBasePath(params.scope)}/pin`, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    credentials: "same-origin",
    body: JSON.stringify({
      slot_key: params.slotKey,
      pinned: params.pinned,
    }),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return (await res.json()) as { ok: true; pinned_slot_keys: string[] }
}
